import { useState } from 'react';
import { useStore } from '../store';
import { templatesApi } from '../services/api';
import { DocumentTemplate, GenericSection } from '../types';
import {
  X,
  Plus,
  Trash2,
  Edit2,
  Check,
  Loader2,
  FileText,
  Copy,
  ChevronDown,
  ChevronUp,
  Save,
  Download,
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function GenericDocumentTemplateModal({ isOpen, onClose }: Props) {
  const { genericSections, setGenericSections, documentTemplates, setDocumentTemplates, addNotification } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isSavingCurrent, setIsSavingCurrent] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [showSaveAsInput, setShowSaveAsInput] = useState(false);

  if (!isOpen) return null;

  const filteredTemplates = documentTemplates.filter((t) =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const refreshTemplates = async () => {
    try {
      const templates = await templatesApi.getAllDocumentTemplates();
      setDocumentTemplates(templates);
    } catch {
      // Silently fail, templates will remain as-is
    }
  };

  const handleLoadTemplate = (template: DocumentTemplate) => {
    setGenericSections(template.sections);
    addNotification(`Loaded template: ${template.name}`, 'success');
    onClose();
  };

  const handleSaveCurrentAsPreset = async () => {
    if (!saveAsName.trim()) return;

    setIsSavingCurrent(true);
    try {
      await templatesApi.createDocumentTemplate({
        name: saveAsName.trim(),
        sections: genericSections,
      });
      addNotification('Current document saved as template', 'success');
      setSaveAsName('');
      setShowSaveAsInput(false);
      await refreshTemplates();
    } catch {
      addNotification('Failed to save as template', 'error');
    } finally {
      setIsSavingCurrent(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document template?')) return;

    setDeletingId(id);
    try {
      await templatesApi.deleteDocumentTemplate(id);
      addNotification('Document template deleted', 'success');
      await refreshTemplates();
    } catch {
      addNotification('Failed to delete template', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRename = async (id: string) => {
    if (!editingName.trim()) return;

    try {
      await templatesApi.updateDocumentTemplate(id, { name: editingName.trim() });
      addNotification('Template renamed', 'success');
      setEditingId(null);
      await refreshTemplates();
    } catch {
      addNotification('Failed to rename template', 'error');
    }
  };

  const handleCreate = async () => {
    if (!newTemplateName.trim()) return;

    setIsCreating(true);
    try {
      const defaultSections: GenericSection[] = [
        {
          id: `section-${Date.now()}`,
          title: 'General Information',
          fields: [
            { id: `field-${Date.now()}-1`, label: 'Name', value: '', type: 'text' },
            { id: `field-${Date.now()}-2`, label: 'Description', value: '', type: 'longText' },
          ],
          isExpanded: true,
          order: 1,
        },
      ];

      await templatesApi.createDocumentTemplate({
        name: newTemplateName.trim(),
        sections: defaultSections,
      });
      addNotification('Document template created', 'success');
      setNewTemplateName('');
      await refreshTemplates();
    } catch {
      addNotification('Failed to create template', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDuplicate = async (template: DocumentTemplate) => {
    try {
      await templatesApi.createDocumentTemplate({
        name: `${template.name} (Copy)`,
        sections: template.sections.map((s) => ({
          ...s,
          id: `${s.id}-copy-${Date.now()}`,
          fields: s.fields.map((f) => ({ ...f, id: `${f.id}-copy-${Date.now()}` })),
        })),
      });
      addNotification('Template duplicated', 'success');
      await refreshTemplates();
    } catch {
      addNotification('Failed to duplicate template', 'error');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-accent" />
            <h2 className="text-lg font-semibold">Document Field Templates</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        {/* Save Current as Template */}
        <div className="p-4 border-b border-gray-100 shrink-0">
          {showSaveAsInput ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                placeholder="Template name..."
                className="input text-sm flex-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveCurrentAsPreset();
                  if (e.key === 'Escape') setShowSaveAsInput(false);
                }}
              />
              <button
                onClick={handleSaveCurrentAsPreset}
                disabled={isSavingCurrent || !saveAsName.trim()}
                className="btn btn-primary flex items-center gap-1.5 text-sm"
              >
                {isSavingCurrent ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                Save
              </button>
              <button
                onClick={() => setShowSaveAsInput(false)}
                className="btn btn-secondary text-sm"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowSaveAsInput(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-accent border border-accent/30 rounded-lg hover:bg-accent/5 transition-colors"
            >
              <Save size={16} />
              Save Current Document as Template
            </button>
          )}
        </div>

        {/* Search & Create */}
        <div className="p-4 border-b border-gray-100 space-y-3 shrink-0">
          <input
            type="text"
            placeholder="Search document templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input text-sm w-full"
          />

          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="New template name..."
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              className="input text-sm flex-1"
            />
            <button
              onClick={handleCreate}
              disabled={isCreating || !newTemplateName.trim()}
              className="btn btn-primary flex items-center gap-1.5 text-sm"
            >
              {isCreating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create
            </button>
          </div>
        </div>

        {/* Template List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FileText size={40} className="mx-auto mb-2 opacity-50" />
              <p>No document templates found</p>
              <p className="text-xs mt-1">Create one or save your current layout.</p>
            </div>
          ) : (
            filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors overflow-hidden"
              >
                {/* Template Header */}
                <div className="flex items-center justify-between p-3 group">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <button
                      onClick={() =>
                        setExpandedId(expandedId === template.id ? null : template.id)
                      }
                      className="p-1 hover:bg-gray-200 rounded shrink-0"
                    >
                      {expandedId === template.id ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>

                    {editingId === template.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(template.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="input text-sm flex-1"
                          autoFocus
                        />
                        <button
                          onClick={() => handleRename(template.id)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-800 truncate">
                          {template.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {template.sections.length} section{template.sections.length !== 1 ? 's' : ''}
                          {' | '}
                          {template.sections.reduce((acc, s) => acc + s.fields.length, 0)} fields
                          {template.updatedAt && (
                            <> | Updated {new Date(template.updatedAt).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  {editingId !== template.id && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleLoadTemplate(template)}
                        className="px-2.5 py-1 text-xs font-medium bg-accent text-white rounded hover:bg-accent/90 transition-colors flex items-center gap-1"
                        title="Load template"
                      >
                        <Download size={12} />
                        Load
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                        title="Duplicate"
                      >
                        <Copy size={14} />
                      </button>
                      {template.isCustom && (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(template.id);
                              setEditingName(template.name);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                            title="Rename"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(template.id)}
                            disabled={deletingId === template.id}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            title="Delete"
                          >
                            {deletingId === template.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Expanded Section Preview */}
                {expandedId === template.id && (
                  <div className="px-4 pb-3 border-t border-gray-200 pt-3 space-y-2">
                    {template.sections.map((section) => (
                      <div key={section.id} className="pl-4 border-l-2 border-accent/30">
                        <p className="text-xs font-medium text-gray-700 mb-1">{section.title}</p>
                        <div className="flex flex-wrap gap-1">
                          {section.fields.map((field) => (
                            <span
                              key={field.id}
                              className="inline-flex items-center px-2 py-0.5 bg-white border border-gray-200 rounded text-[11px] text-gray-600"
                            >
                              <span className="text-gray-400 mr-1">
                                {field.type === 'longText' ? 'Aa' : field.type === 'date' ? 'D' : field.type === 'number' ? '#' : 'T'}
                              </span>
                              {field.label}
                              {field.required && <span className="text-red-400 ml-0.5">*</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
